import { Link } from "react-router";

import { AppLayout } from "@/components/layout/app-layout";
import { getModuleStatus, type Module, MODULES } from "@/data/modules";
import { cn } from "@/lib/utils";

function StatusBadge({ status }: { status: ReturnType<typeof getModuleStatus> }) {
  if (status === "completed") {
    return (
      <span className="bg-muted/20 text-muted inline-block rounded-full px-2.5 py-0.5 text-xs font-medium">
        Completed
      </span>
    );
  }
  if (status === "current") {
    return (
      <span className="bg-primary inline-block rounded-full px-2.5 py-0.5 text-xs font-medium text-white">
        Current
      </span>
    );
  }
  return (
    <span className="border-border text-muted inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium">
      Upcoming
    </span>
  );
}

function ModuleCard({ module }: { module: Module }) {
  const status = getModuleStatus(module);
  const isCurrent = status === "current";

  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden rounded-2xl bg-white",
        isCurrent ? "ring-primary shadow-xl ring-2" : "shadow-md",
      )}
    >
      {/* Top accent bar */}
      <div className={cn("h-1.5 w-full", isCurrent ? "bg-primary" : "bg-muted/30")} />

      <div className="flex flex-1 flex-col p-6">
        <div className="mb-3 flex items-start justify-between gap-2">
          <p className="text-muted text-xs font-semibold tracking-wider uppercase">
            Module {module.id}
          </p>
          <StatusBadge status={status} />
        </div>

        <h3 className="font-heading text-foreground text-lg leading-snug font-bold">
          {module.title}
        </h3>

        <p className="text-muted mt-1 text-sm">
          {module.location} <span className="mx-1">•</span> {module.month}
        </p>

        <p className="text-muted mt-3 flex-1 text-sm leading-relaxed italic">{module.outcome}</p>

        <div className="mt-5">
          <Link
            to={`/modules/${module.id}`}
            className="text-primary text-sm font-medium hover:underline"
          >
            Open →
          </Link>
        </div>
      </div>
    </div>
  );
}

export function ProgramOverviewPage() {
  return (
    <AppLayout>
      <div className="mx-auto max-w-7xl px-6 py-12">
        {/* Hero */}
        <div className="mb-8">
          <p className="text-primary mb-2 text-xs font-semibold tracking-[0.24em] uppercase">
            Innovation Leadership Program
          </p>
          <h1 className="font-heading text-foreground text-3xl font-bold md:text-4xl">
            Tesla Program
          </h1>
          <p className="text-muted mt-3 max-w-2xl text-base leading-relaxed">
            A four-module journey from challenge scoping to pitch-ready innovation. Each module
            builds on the last — combining in-person workshops with structured inter-module work.
          </p>
        </div>

        {/* Module cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {MODULES.map((module) => (
            <ModuleCard key={module.id} module={module} />
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
